import { CalculatorStore, useCalculatorStore } from '@/store/calculatorStore'
import React from 'react'
import client from '@/utils/api-client'
import { calculateResultsFromState } from './useCalculations'
import * as Sentry from '@sentry/react'
import useDebounce from './useDebounce'

export const DEBOUNCE_DELAY_MS = 5000

function detectBrowser(): string | null {
  if (typeof window === 'undefined') {
    return null
  }

  const userAgent = window.navigator.userAgent.toLowerCase()

  if (
    userAgent.includes('edg/') ||
    userAgent.includes('edgios/') ||
    userAgent.includes('edga/')
  ) {
    return 'edge'
  }

  if (userAgent.includes('firefox/') || userAgent.includes('fxios/')) {
    return 'firefox'
  }

  if (
    (userAgent.includes('chrome/') || userAgent.includes('crios/')) &&
    !userAgent.includes('edg/') &&
    !userAgent.includes('edgios/') &&
    !userAgent.includes('edga/')
  ) {
    return 'chrome'
  }

  if (
    userAgent.includes('android') &&
    userAgent.includes('version/') &&
    userAgent.includes('safari/') &&
    !userAgent.includes('chrome/') &&
    !userAgent.includes('chromium/') &&
    !userAgent.includes('crios/') &&
    !userAgent.includes('firefox/') &&
    !userAgent.includes('fxios/') &&
    !userAgent.includes('opr/') &&
    !userAgent.includes('opera') &&
    !userAgent.includes('samsungbrowser/')
  ) {
    return 'android'
  }

  if (
    userAgent.includes('safari/') &&
    !userAgent.includes('chrome/') &&
    !userAgent.includes('chromium/')
  ) {
    return 'safari'
  }

  return 'other'
}

function detectDeviceView(): 'mobile' | 'desktop' {
  if (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(min-width: 1100px)').matches
  ) {
    return 'desktop'
  }

  return 'mobile'
}

export function saveState(
  state: CalculatorStore,
  calculatedDiplomas: Record<string, unknown>
) {
  return Sentry.startSpan(
    {
      op: 'http.client',
      name: 'POST /anon-api/users/calculator',
    },
    async (sentrySpan) => {
      const { logger } = Sentry

      sentrySpan.setAttribute('uuid', state.uuid)
      sentrySpan.setAttribute('diploma_type', state.inputDiploma)
      sentrySpan.setAttribute(
        'num_courses',
        state.courses.filter((c) => c.included).length
      )
      sentrySpan.setAttribute('has_email', !!state.userDetails.email)
      sentrySpan.setAttribute('app_version', import.meta.env.VITE_APP_VERSION)

      try {
        const numCoursesIncluded = state.courses.filter(
          (course) => course.included && course.visible !== false
        ).length
        const browser = detectBrowser()
        const deviceView = detectDeviceView()

        const result = await client('/anon-api/users/calculator', {
          data: {
            calculatorState: state,
            phone: state.userDetails.phone,
            userEmail: state.userDetails.email,
            uuid: state.uuid,
            message: state.userDetails.message,
            version: import.meta.env.VITE_APP_VERSION,
            lastPath: '/',
            userAdmin: false,
            browser,
            deviceView,
            calculatedDiplomas: calculatedDiplomas,
            birthyear: state.userDetails.birthyear,
            studyStartYear: state.userDetails.studyStartYear,
            hasUsedAiAnalyzer: state.hasUsedAiAnalyzer,
            inputDiploma: state.inputDiploma,
            improvedDiploma: state.improvedDiploma,
            numCoursesIncluded,
          },
        })

        sentrySpan.setAttribute('success', true)
        logger.info('Calculator state saved successfully', {
          uuid: state.uuid,
          numCourses: state.courses.filter((c) => c.included).length,
        })

        return result
      } catch (error) {
        sentrySpan.setAttribute('success', false)
        Sentry.captureException(error, {
          tags: {
            component: 'SaveState',
            action: 'saveCalculatorState',
          },
          extra: {
            uuid: state.uuid,
            diplomaType: state.inputDiploma,
            numCourses: state.courses.filter((c) => c.included).length,
            hasUserData: !!(state.userDetails.email || state.userDetails.phone),
          },
        })

        logger.error('Failed to save calculator state', {
          uuid: state.uuid,
          error: (error as Error).message,
        })

        throw error
      }
    }
  )
}

function useSaveState() {
  const setStatus = useCalculatorStore((state) => state.setStatus)
  const setUuid = useCalculatorStore((state) => state.setUuid)
  const state = useCalculatorStore((state) => state)

  // Ensure UUID is set (only runs once on mount)
  React.useEffect(() => {
    setUuid()
  }, [setUuid])

  // Only debounce the state - calculate results from debounced state to ensure consistency
  const debouncedState = useDebounce(state, DEBOUNCE_DELAY_MS)

  React.useEffect(() => {
    // debouncedState.status becomes 'modified' when user makes changes to calculator state
    // After debounce delay (5s), this triggers the auto-save operation
    if (debouncedState.status !== 'modified') {
      return
    }

    // Calculate results from the debounced state to ensure consistency
    const calculatedDiplomas = calculateResultsFromState(debouncedState)

    saveState(debouncedState, calculatedDiplomas)
      .then(() => {
        setStatus('saved')
      })
      .catch((error) => {
        Sentry.captureException(error, {
          tags: {
            component: 'UseSaveState',
            action: 'autoSave',
          },
          extra: {
            uuid: debouncedState.uuid,
            status: debouncedState.status,
          },
        })
      })
  }, [debouncedState, setStatus])

  React.useEffect(() => {
    if (state.status !== 'saved') {
      return
    }

    const params = new URLSearchParams(window.location.search)
    params.set('uuid', state.uuid)
    window.history.replaceState(null, '', `?${params.toString()}`)
  }, [state.status, state.uuid])
}

export default useSaveState
